import { useState } from 'react';
import { Check, FileCheck, ShieldCheck } from 'lucide-react';
import Header from 'components/Profile/Header';
import ViewFooter from 'components/Profile/View/Footer';
import { getStepById } from 'pages/Profile/helper';
import Card from './Card';
import TreatmentConsentList from './TreatmentConsents/List';
import TreatmentConsentModal from './TreatmentConsents/Modal';
import { isSigned, pendingForms, signedForms, acceptedLabel, signedLabel, authorizedLabel } from './helper';

const Landing = ({ consentForms = [], treatmentConsents = [], authorizedCount = 0, onOpenForms, onOpenAuthorizations, onTreatmentConsentSigned }) => {
  const step = getStepById('consent');
  const signed = isSigned(consentForms);
  const [activeConsent, setActiveConsent] = useState(null);

  const pending = pendingForms(treatmentConsents);
  const signedTreatments = signedForms(treatmentConsents);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-7 pt-5 lg:px-10">
        <div className="mb-5">
          <Header
            icon={step.icon}
            iconBg={step.iconBg}
            iconColor={step.iconColor}
            title={step.title}
            description={step.subtitle}
            completed={signed}
          />
        </div>

        <TreatmentConsentList
          title="Needs action"
          description="Sign before your appointment so we can start treatment on time."
          badge={<span className="shrink-0 text-sm font-bold text-amber-700">{pending.length} to sign</span>}
          consents={pending}
          onOpen={setActiveConsent}
        />

        <div className="grid grid-cols-1 gap-3 animate-slide-up sm:grid-cols-2">
          <Card
            icon={FileCheck}
            iconClassName={signed ? 'bg-success-100 text-success-600' : 'bg-amber-100 text-amber-600'}
            badge={acceptedLabel(consentForms)}
            badgeClassName={signed ? 'bg-success-50 text-success-700' : 'bg-amber-50 text-amber-700'}
            title="Consent forms"
            description={signed
              ? 'All your agreements are accepted and kept on file for your records. You can re-read or download any signed document at any time.'
              : 'The agreements every patient accepts before treatment: how we protect your health information, how we use your personal data, your consent to care and billing terms, and how we may contact you.'}
            cta={signed ? 'View signed documents' : 'Review & accept'}
            onClick={onOpenForms}
          />

          <Card
            icon={ShieldCheck}
            iconClassName="bg-success-100 text-success-600"
            badge={authorizedLabel(authorizedCount)}
            badgeClassName="bg-success-50 text-success-700"
            title="Authorizations"
            description="Under HIPAA, our staff can only discuss your care with people you authorize. Add family members or caregivers, choose what they can ask about, and revoke access at any time."
            cta="Manage who can access your info"
            onClick={onOpenAuthorizations}
          />
        </div>

        <div className="mt-5">
          <TreatmentConsentList
            title="Treatment consents"
            badge={
              <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-success-200 bg-success-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-success-700">
                <Check size={12} strokeWidth={3} /> {signedLabel(treatmentConsents)}
              </span>
            }
            consents={signedTreatments}
            onOpen={setActiveConsent}
          />
        </div>
      </div>

      <ViewFooter stepId="consent" />

      {activeConsent && (
        <TreatmentConsentModal
          consent={activeConsent}
          onClose={() => setActiveConsent(null)}
          onSigned={onTreatmentConsentSigned}
        />
      )}
    </div>
  );
};

export default Landing;
